/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useReducer } from 'react';
import { Paper, Grid, makeStyles } from '@material-ui/core';
import { object, func } from 'prop-types';
import { useQuery } from '@apollo/client';
import { uid } from 'react-uid';

import {
    OptionTree,
    valuesLib,
} from '../../../../../../helpers/vaseConfigurations';

import findVaseConfigContent from '../../../../../../gql/queries/findVaseConfigContent';
import noop from '../../../../../../helpers/noop';

import OptionCarousel from './OptionCarousel';

const useStyles = makeStyles((theme) => ({
    root: {
        marginBottom: '20px',
        width: '100%',
        color: theme?.palette?.common?.black,
        maxWidth: '690px',

        [theme.breakpoints.down(1100)]: {
            maxWidth: '550px',
        },
        [theme.breakpoints.down(850)]: {
            maxWidth: '450px',
        },
        [theme.breakpoints.down(600)]: {
            maxWidth: '100%',
        },
    },
    paperHeader: {
        boxSizing: 'border-box',
        width: '100%',
        height: '40px',
        padding: '12px 20px',
        fontWeight: '600',
        fontSize: '13px',
        lineHeight: '19px',
        background: theme?.palette?.colorNeutral30,
        borderRadius: '4px 4px 0 0',
    },
    paperBody: {
        boxSizing: 'border-box',
        padding: '20px 20px 20px 20px',
    },
    radio: {
        color: theme.palette.cms?.primary || '#693388',
        padding: '6px 11px',
        '&$checked': {
            color: theme.palette.cms?.primary || '#693388',
        },
        [theme.breakpoints.down('600')]: {
            '& svg': {
                fontSize: '24px',
            },
        },
    },
    checked: {
        color: theme.palette.cms?.primary || '#693388',
        '&$checked': {
            color: theme.palette.cms?.primary || '#693388',
        },
    },
    skuLabelName: {
        color: '#000',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
        },
    },
    skuLabelAmount: {
        color: '#585858',
        marginLeft: '10px',
        fontWeight: '500',
        [theme.breakpoints.down('600')]: {
            fontSize: '15px',
            textAlign: 'right',
        },
        '&--retail': {
            marginLeft: '5px',
            textDecoration: 'line-through',
            color: '#ed0000',
        },
        '&--sale': {
            marginLeft: '5px',
            color: '#2e8540',
        },
    },
    formLabel: {
        display: 'flex',
        width: '100%',
        '& >div': {
            width: '100%',
            justifyContent: 'space-between',
            display: 'flex',
        },
    },
    upSellMessgaeRadioButton: {
        background: theme?.palette?.colorNeutral30,
    },
}));

/**
 * @description Pulls the product options off of the skus and returns and array
 * with the sku attached.
 * @param {[{}]]} productSkus
 * @returns
 */
function extractInfo(productSkus) {
    const skuOptionTranslations = {
        bear: 'brown_bear',
        chocolate: 'sending_smiles_chocolate',
        cookie: 'birthday_cookie',
    };

    const optionsToRemove = {
        wrap: true,
        none: true,
    };

    // eslint-disable-next-line class-methods-use-this
    function cleanOption(string) {
        if (!string || optionsToRemove[string]) {
            return false;
        }
        return true;
    }

    function getOptionsFromSku(sku) {
        // find stem count
        const stemCount = sku.attributes.reduce((acc, attr) => {
            if (acc === '' && attr.name === 'stem_count') {
                return attr.values[0].value;
            }
            return acc;
        }, '');

        // make it a recognized value
        const size = `stems_${stemCount}`;
        let containerType = sku.containerType.filter(cleanOption);
        if (!containerType.length) {
            containerType = [valuesLib.noVase];
        }

        const [occasion] = sku.occasion.filter(cleanOption);
        let bouquetIncludes = sku.bouquetIncludes.filter(cleanOption);

        // configure balloons and other attributes
        bouquetIncludes = bouquetIncludes.map((attr) => {
            if (attr === 'balloon') {
                return `${occasion}_balloon`;
            }

            if (skuOptionTranslations[attr]) {
                return skuOptionTranslations[attr];
            }

            return attr;
        });

        const totalAttributes = [
            size,
            ...bouquetIncludes,
            ...containerType,
        ];

        return totalAttributes;
    }

    return productSkus.map((sku) => ({
        sku,
        options: getOptionsFromSku(sku),
    }));
}

/**
 * @description Converts the CMS option info into a map of options
 * along with their respective weights.
 * @param {[{}]} optionInfo
 * @returns
 */
function indexBasedOptionWeight(optionInfo) {
    const accumulator = {
        [valuesLib.noVase]: 2,
    };

    if (!optionInfo?.selection_tier?.reduce) {
        return accumulator;
    }

    return optionInfo.selection_tier.reduce((acc, tier, index) => {
        const newAcc = { ...acc };

        tier.tier.tier_options.forEach((option) =>  {
            const value = option.option[0].value;
            newAcc[value] = index + 1;
        });

        return newAcc;
    }, accumulator);
}

/**
 * Blanket configuration for all product skus. Many
 * of these will be filtered from the UI after the OptionTree
 * is applied.
 */
const initialState = {
    skuFromSelection: undefined,
    tree: null,
    tiers: [
        {
            title: 'Select Size',
            id: uid('Select Size', 0), // TODO do better
            default: true, // should this tier initialize to a defaulted selection
            multiSelect: false, // does this tier allow for multiple selection within the tier
            selected: [valuesLib.stems24], // list of selected value(s)
            canDeselect: false, // should a user be able to de-select a selected option
            toggleOptions: false, // should this tier disable its option
            hideOptions: false, // wether or not logic should be applied to hide options, initial state never changes
            gatingIndex: -1, // selection in tier index X is required for this to show
            options: [ // all the possible options within this tier, changes based on availability from recieved skus
                {
                    value: valuesLib.stems12,
                    enabled: true,
                    hidden: false,
                },
                {
                    value: valuesLib.stems24,
                    enabled: true,
                    hidden: false,
                },
            ],
        },
        {
            title: 'Select Vase',
            id: uid('Select Vase', 1),
            default: true, // should this tier initialize to a defaulted selection
            multiSelect: false, // does this tier allow for multiple selection within the tier
            selected: [], // list of selected value(s)
            canDeselect: false, // should a user be able to de-select a selected option
            toggleOptions: false, // should this tier disable its option
            hideOptions: true, // wether or not logic should be applied to hide options, initial state never changes
            gatingIndex: -1, // selection in tier index X is required for this to show
            options: [ // all the possible options within this tier, changes based on availability from recieved skus
                {
                    value: valuesLib.noVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.clearVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.clearHourglassVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.pinkHourglassVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.redFootedVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.redHourglassVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.purpleFootedVase,
                    enabled: true,
                    hidden: true,
                },
                {
                    value: valuesLib.waterfordVase,
                    enabled: true,
                    hidden: true,
                },
            ],
        },
        {
            title: 'Select Specialty Item',
            id: uid('Select Specialty Item', 2), // TODO do better
            default: false, // should this tier initialize to a defaulted selection
            multiSelect: true, // does this tier allow for multiple selection within the tier
            selected: [], // list of selected value(s)
            canDeselect: true, // should a user be able to de-select a selected option
            toggleOptions: true, // should this tier disable its option
            hideOptions: true, // wether or not logic should be applied to hide options, initial state never changes
            gatingIndex: 1, // selection in tier index X is required for this to show
            options: [ // all the possible options within this tier, changes based on availability from recieved skus
                {
                    value: valuesLib.brownBear,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.loveBalloon,
                        valuesLib.newBabyBalloon,
                        valuesLib.congratsBalloon,
                        valuesLib.thankYouBalloon,
                        valuesLib.getWellBalloon,
                        valuesLib.birthdayBalloon,
                    ],
                },
                // {
                //     value: valuesLib.bear,
                //     enabled: true,
                //     hidden: false,
                //     multiSelect: [
                //         valuesLib.loveBalloon,
                //         valuesLib.newBabyBalloon,
                //         valuesLib.congratsBalloon,
                //         valuesLib.thankYouBalloon,
                //         valuesLib.getWellBalloon,
                //         valuesLib.birthdayBalloon,
                //     ],
                // },
                {
                    value: valuesLib.candle,
                    enabled: true,
                    hidden: false,
                    multiSelect: [],
                },
                {
                    value: valuesLib.loveBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
                {
                    value: valuesLib.newBabyBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
                {
                    value: valuesLib.congratsBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
                {
                    value: valuesLib.thankYouBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
                {
                    value: valuesLib.getWellBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
                {
                    value: valuesLib.birthdayBalloon,
                    enabled: true,
                    hidden: false,
                    multiSelect: [
                        valuesLib.brownBear,
                        valuesLib.bear,
                    ],
                },
            ],
        },
        {
            title: 'Select Sweet Treat', // tier display title
            id: uid('Select Sweet Treat', 3), // TODO do better
            default: false, // should this tier initialize to a defaulted selection
            multiSelect: false, // does this tier allow for multiple selection within the tier
            selected: [], // list of selected value(s)
            canDeselect: true, // should a user be able to de-select a selected option
            toggleOptions: true, // should this tier disable its option
            hideOptions: true, // wether or not logic should be applied to hide options, initial state never changes
            gatingIndex: 1, // selection in tier index X is required for this to show (different from hide options)
            options: [ // all the possible options within this tier, changes based on availability from recieved skus
                {
                    value: valuesLib.chocolate,
                    enabled: true,
                    hidden: false,
                },
                {
                    value: valuesLib.cookie,
                    enabled: true,
                    hidden: false,
                },
                {
                    value: valuesLib.godivaChocolate,
                    enabled: true,
                    hidden: false,
                },
            ],
        },
    ],
};

function addNewSelection(state, data) {
    const tiers = [...state.tiers];
    const index = tiers.findIndex((tier) => tier.id === data.id);
    const oldTier = state.tiers[index];

    // copy
    const newTier = {
        ...oldTier,
    };

    if (newTier.multiSelect) {
        // only valid options will be enabled making this simply a push
        newTier.selected.push(data.value);
    } else {
        newTier.selected[0] = data.value; // only allowed a single selection
    }

    // point to new tier state
    tiers[index] = newTier;

    return tiers;
}

function removeSelection(state, data) {
    const tiers = [...state.tiers];
    const index = tiers.findIndex((tier) => tier.id === data.id);
    const oldTier = state.tiers[index];

    // copy
    const newTier = {
        ...oldTier,
    };

    // filter out the existing selection
    newTier.selected = newTier.selected.filter((value) => value !== data.value);

    // point to new tier state
    tiers[index] = newTier;

    return tiers;
}

function revertSelections(state, oldSelectionValues) {
    const tiers = [...state.tiers];
    let shouldWipe = false;

    for (let i = 0; i < tiers.length; i += 1) {
        const oldValue = oldSelectionValues[i];
        const newValues = tiers[i].selected;

        // wipe everything after the changed tier
        if (shouldWipe) {
            tiers[i].selected = [];
        }

        // identify where the new selection is different
        if (oldValue && !newValues.includes(oldValue)) {
            shouldWipe = true;
        }
    }

    return tiers;
}

/**
 * @description Provides a map of still possible multi select options.
 * @param {{}} state
 * @returns
 *
 * @note
 * MultiSelect has an edge case where:
 * - a multi select options is unavaiable and should always be disabled
 * - a multi select option ends on a path where no other multi select is technically a remaining option
 *
 * This combination causes a state where the follow example accessible through a single path
 * - selection: [stems24, clearVase, getWellBalloon, bear]
 *
 * But should also be accessible through:
 * - selection: [stems24, clearVase, bear, getWellBalloon]
 *
 * Since bear technically has no more remaining options, balloons become disabled. Also since some
 * might be unavailable and should always be disabled, we cannot rely on just looking at valid
 * multi select options.
 *
 * To solve this, on a multi select tier, we must cross reference whats available with the valid
 * multi select options and keep valid ones that are available present despite them technically
 * not being valid according the the tree
 */
function buildMultiSelectionMap({
    multiSelect,
    selected,
    selection,
    state,
}) {
    const availableMultiSelectOptions = {};

    if (!multiSelect || !selected.length) {
        return availableMultiSelectOptions;
    }

    // 1. re-create the selection without the multi tier
    const filteredSelection = selection.filter((value) => !selected.includes(value));

    // 2. find the remaining options from the filtered
    const filteredRemainingOptions = state.tree.getOptionsFromSelection(filteredSelection);

    // 3. mark the remaining options for the multi select tier as available
    filteredRemainingOptions.forEach((option) => {
        availableMultiSelectOptions[option] = true;
    });

    return availableMultiSelectOptions;
}

function findRemainingOptions(state) {
    // get the remaining valid options from the tree
    const selection = state.tiers
        // option tree only accepts an array of values that represent a selection path
        .map((tier) => tier.selected)
        // no selection is represented by empty array and shouldnt be passed
        .filter((value) => value.length > 0)
        // get rid of nested array for simplicity
        .flat();

    // grab the remaining options based on the new selection
    const remainingOptions = state.tree.getOptionsFromSelection(selection);
    const optionsToCompleteSku = {};

    if (!state.skuFromSelection) {
        const pathsToCompleteSku = state.tree.getPathsThatContainSelection(selection).flat();
        // only use unqique options

        pathsToCompleteSku.forEach((option) => {
            optionsToCompleteSku[option] = true;
        });
    }

    // use the remainingOption to toggle enabled map
    // toggle the options IF the tier allows it
    const updatedOptionStates = state.tiers.map((tier) => {
        const {
            toggleOptions,
            hideOptions,
            selected,
            multiSelect,
        } = tier;

        if (!toggleOptions && !hideOptions) {
            return tier;
        }

        const tierHasSelection = selected.length > 0;

        const availableMultiSelectOptions = buildMultiSelectionMap({
            multiSelect,
            selected,
            selection,
            state,
        });

        const toggledOptions = tier.options.map((option) => {
            let enabled = option.enabled;
            let hidden = option.hidden;
            const validMultiSelections = option.multiSelect;

            const optionNotRemaining = !remainingOptions.includes(option.value);
            const optionIsSelected = selected.includes(option.value);
            const optionCompletesSku = optionsToCompleteSku[option.value];

            /**
             * There is a scenario where the final option of a sku
             * is selected, and because of that, everything else is disabled.
             *
             * This presents a problem where the configuration is incomplete
             * but there are no options enabled to click on.
             *
             * In this scenario, we need to keep the options to reach the desired end product
             * available for the user to continue selecting.
             */

            if (toggleOptions && multiSelect) {
                // if this tier allows multi selection, we need to hide invalid options and allow valid

                // 1. is one of the ties' selected values part of this option's valid multi select configuration?
                const multiSelectOption = selected.filter((value) => validMultiSelections.includes(value)).length;

                // 2. is one of the tiers' selected option's in conflict with the valid multi select configuration?
                const invalidMultiSelect = selected.filter((value) => !validMultiSelections.includes(value)).length;

                /**
                 * - cascading logic checks for enabled/disabled
                 * - Order of checks here is suuuuper important
                 * - all conditions mutually exclusive
                 */

                // 3. if there is no selection and the particular option is still a valid based on tree results, should be enabled
                if (!tierHasSelection && !optionNotRemaining) {
                    enabled = true;

                // 4. a selected option should never be disabled
                } else if (optionIsSelected) {
                    enabled = true;

                // 5. if this specific option is a valid multi select option
                //    BUT another valid multi select option is ALREADY selected, should be disabled
                //    note: this must run before the next statement or it wont ever get picked up
                } else if (!optionIsSelected && invalidMultiSelect) {
                    enabled = false;

                // 6. if the option is no longer valid based on tree results, disable the option unless its available
                }  else if (optionNotRemaining && !availableMultiSelectOptions[option.value]) {
                    enabled = false;

                // 7. a selected value found in this specific option's valid multi select should remain enabled
                //    we have already checked for "valid" invalid multi selects in step 5
                } else if (!optionIsSelected && multiSelectOption) {
                    enabled = true;
                }

            // single selection only
            } else if (toggleOptions) {
                // 1. no options are remaining (figured out before) AND its not the eselected option, disabled
                if (optionNotRemaining && !optionIsSelected) {
                    enabled = false;

                // 2. there are remaining options or its selected
                } else {
                    enabled = true;
                }
            }

            if (!state.skuFromSelection && optionCompletesSku) {
                enabled = true;
            }

            if (hideOptions) {
                // we dont want to show options that are literally impossible, only ones that could change as we are updating
                // to do this we need to
                // 1. walk back to the gating tier and find the remaining options
                const fromFirstSelection = selection.slice(0, 1);
                const optionsFromInitial = state.tree.getOptionsFromSelection(fromFirstSelection);

                const optionIsImpossible = !optionsFromInitial.includes(option.value);
                const optionIsNoVase = option.value === valuesLib.noVase;

                if (optionIsImpossible && !optionIsNoVase) {
                    hidden = true;
                } else {
                    hidden = false;
                }
            }

            return {
                ...option,
                enabled,
                hidden,
            };
        });

        return {
            ...tier,
            options: toggledOptions,
        };
    });

    return updatedOptionStates;
}

const localReducer = (state, action) => {
    const newState = { ...state };

    if (action.type === 'initializeState') {
        const optionWeights = indexBasedOptionWeight(action.payload.optionInfo);

        const ot = new OptionTree({}, optionWeights);

        const skuTuple = extractInfo(action.payload.productSkus);

        // build/find all option paths and configuration for the given product
        ot.buildOptionTree(skuTuple);

        newState.tree = ot;

        const cmsOptionInfo = action.payload.optionInfo.selection_options;
        const cmsTiers = action.payload.optionInfo.selection_tier;

        /**
         * information on options comes from cms and initial state is
         * defined here. Need to merge the two appropriatly
         *
         * Options array should be from CMS
         * Option info should remain with initial state added on
         * tier title should be set from cms
         * options should first be set, then filtered based on availability
         *
         */

        // 1. create a tier from initial state that contains state properties
        // TODO: this will be reconciled by index, should have a unique id though
        const newTiers = newState.tiers.map((tier, index) => {
            const newTier = { ...tier };
            const cmsTier = cmsTiers[index]?.tier;

            // only if a tier is found from cms data, otherwise default defined locally
            if (!cmsTier) {
                return newTier;
            }

            // 2. set title, grab the options
            newTier.title = cmsTier.title;
            newTier.options = newTier.options.map((localOption) => {
                // find the tier's options
                const cmsOption = cmsTier.tier_options.find((target) => target.option[0].value === localOption.value);

                // local state has all possible option which will be filtered next step
                // if we dont have a match for cms, just return the localOption
                if (!cmsOption) {
                    return localOption;
                }

                // go find the option info that has the price
                const cmsOptionProductSpecific = cmsOptionInfo.find((target) => cmsOption.option[0].value === target.option.option_info[0].value);

                // put it all together
                const newLocalOption = {
                    ...localOption,
                    cmsInfo: {
                        displayValue: cmsOption.option[0].display_value,
                        image: cmsOptionProductSpecific.option.product_specific_image || cmsOption.option[0].image,
                        hardcodedPrice: cmsOptionProductSpecific.option.hardcoded_price,
                    },
                };

                return newLocalOption;
            });

            return newTier;
        });

        const filteredTiers = newTiers.map((tier) => {
            const remainingOptions = tier.options.filter(({ value }) => newState.tree.uniqueOptions[value]);
            let newSelected = tier.selected;
            /**
             * If the particular tier has default selections enabled,
             * reconcile the fact that the defautl selected is not currently available
             * and shift it to the first available option
             */
            if (tier.default && remainingOptions.length) {
                // By default set largest option of select size
                const setDefult = remainingOptions.find((block) => block.value === tier.selected[0]);
                newSelected =  setDefult ? [setDefult.value] :  [remainingOptions[0].value];
            }

            return {
                ...tier,
                selected: newSelected,
                options: remainingOptions,
            };
        });

        // update with filtered tiers
        newState.tiers = filteredTiers;

        // now that selection is finalized, get list of selection values
        const selection = newState.tiers
            .map((tier) => tier.selected);

        // get the sku for the given default selection if there is one
        newState.skuFromSelection = newState.tree.getSkuFromSelection(selection);

        // account for the default selections and find remaining options
        const tiersWithDefaultSelection = findRemainingOptions(newState);

        newState.tiers = tiersWithDefaultSelection;

        return newState;
    }

    if (action.type === 'select') {
        /**
         * 1. Make reference of old selection
         * 2. set the new selection
         * 3. diff the selections and wipe out low tiers on high tier selection
         * 4. update final selection based on selection diff
         */

        // 1. pull out the old "selection"
        const oldSelectionValues = newState.tiers
            .map((tier) => tier.selected)
            .flat();

        // 2. set the new selection
        newState.tiers = addNewSelection(newState, action.payload);

        // 3. and 4. diff the selections and revert low tiers to unselected to prevent bad selections
        newState.tiers = revertSelections(newState, oldSelectionValues);

        // now that selection is finalized, get list of selection values
        const selection = newState.tiers
            .map((tier) => tier.selected);

        // 6. get the sku for the given selection if there is one
        newState.skuFromSelection = newState.tree.getSkuFromSelection(selection);

        // 5. update the tiers remaining options based on the new selection
        newState.tiers = findRemainingOptions(newState);

        return newState;
    }

    if (action.type === 'deselect') {
        // 1. pull out the old "selection"
        const oldSelectionValues = newState.tiers
            .map((tier) => tier.selected)
            .flat();

        // 2. set the new selection
        newState.tiers = removeSelection(newState, action.payload);

        // 3. and 4. diff the selections and revert low tiers to unselected to prevent bad selections
        newState.tiers = revertSelections(newState, oldSelectionValues);

        // now that selection is finalized, get list of selection values
        const selection = newState.tiers
            .map((tier) => tier.selected);

        // 6. get the sku for the given selection if there is one
        newState.skuFromSelection = newState.tree.getSkuFromSelection(selection);

        // 5. update the tiers remaining options based on the new selection
        newState.tiers = findRemainingOptions(newState, oldSelectionValues);

        return newState;
    }

    return state;
};

const VaseConfigurator = (props) => {
    const {
        product,
        setVaseConfiguratorSku,
    } = props;
    const classes = useStyles();

    const VASE_CONFIG_CONTENT = findVaseConfigContent({
        brand: '1800flowers',
        contentType: 'vase_configurator',
        query: `\\"title\\" : \\"${product.name}\\"`,
    });

    const { data, loading, error } = useQuery(VASE_CONFIG_CONTENT);

    /**
     * An object to represent the which tier we want the option
     * to display in. Order is also established here for the specific keys.
     */
    const [state, localDispatch] = useReducer(localReducer, JSON.parse(JSON.stringify(initialState)));
    const dispatchHandler = (type, payload) => {
        localDispatch({ type, payload });
    };

    useEffect(() => {
        if (error) {
        // TODO MBP LOGGEr
            return noop;
        }

        if (loading) {
            return noop;
        }

        const optionInfo = data?.findContent?.content?.entries?.find((entry) => entry.title === product.name);

        dispatchHandler('initializeState', {
            productSkus: product.productSkus,
            optionInfo,
        });

        return noop;
    }, [
        product,
        data,
        loading,
        error,
    ]);

    useEffect(() => {
        setVaseConfiguratorSku(state.skuFromSelection);
    }, [state.skuFromSelection, setVaseConfiguratorSku]);

    if (state.tree === null) {
        return null;
    }

    return (
        <Paper elevation={1} className={classes.root}>
            <div className={classes.paperHeader}>
                1. Pick Your Item
            </div>
            <div className={classes.paperBody}>
                <Grid
                    container
                    direction="column"
                    justify="flex-start"
                >
                    {state.tiers.map((tier) => {
                        // check to see if previous tier is gating and decide
                        // on wether or not to show subsequent tiers
                        const dependentTier = state.tiers[tier.gatingIndex];
                        if (dependentTier && !dependentTier.selected.length) {
                            return null;
                        }

                        return (
                            <OptionCarousel
                                key={tier.id}
                                data={tier}
                                localStateDispatcher={dispatchHandler}
                            />
                        );
                    })}

                </Grid>
            </div>
        </Paper>
    );
};

VaseConfigurator.propTypes = {
    product: object.isRequired,
    setVaseConfiguratorSku: func.isRequired,
};

export default VaseConfigurator;
